'use client';

import React from 'react';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from '@/i18n/useOTSTranslation';
import {
  IRegionResortsPaginated, IRegionShort, IRegionFeaturedResort,
} from '@/api/region/types';
import { IRelatedNews, ResortStatus } from '@/api/resort/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { useRegionLayoutBubble } from '@/page-components/region/_context';
import { IRegionLayout } from '@/page-components/region/_layout';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { Routes } from '@/routes';
import PlanYourTrip from '@/components/common/PlanYourTrip/RegionWidget';
import { Hero400Error } from '@/components/400';
import { formatDateFromStr, MINIMAL_DATE_FORMAT } from '@/util/formatDate';
import { spansMultipleRegions } from '@/util/spansMultipleRegions';
import { LoadMoreRows } from '@/components/region/LoadMoreRows';
import { useLoadMoreResorts } from '@/components/region/useLoadMoreResorts';
import { fetchRegionResortsByStatus } from '@/api/region/static';
import { REGION_RESORT_PAGE_SIZE } from '@/ots-constants';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { PageBody } from '@/components/common/PageBody';
import { SeoSection } from '@/components/common/SeoSection';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import {
  OpenResortsStatsTable,
  ProjectedOpeningTable,
} from '@/components/region/StatusTables';

export type RegionOpenResortsPageProps = {
  resorts: IRegionResortsPaginated,
  openingDatesResorts: IRegionResortsPaginated | null,
  openingDate: string,
  numResorts: number,
  pathInfo: IRegionShort,
  relatedNews: IRelatedNews | null,
  featured: IRegionFeaturedResort,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-open-resorts-page';
export const TEMPLATE_ID = '2.1.3';
export const TEMPLATE_NAME = 'Region Open Resorts';

export default function RegionOpenResortsPage({
  openingDate, resorts, openingDatesResorts, pathInfo, numResorts, relatedNews,
}: RegionOpenResortsPageProps) {
  useRegionLayoutBubble({});
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  if (!resorts) {
    return <Hero400Error light />;
  }

  const seoTitle = pathInfo.isLogical ? t('region.openResorts.logicSeoTitle', { region: pathInfo.title })
    : t('region.openResorts.seoTitle', { region: pathInfo.title });

  const seoText = pathInfo.isLogical ? m('region.openResorts.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  }) : m('region.openResorts.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  });

  const fetchOpenPage = React.useCallback(
    (page: number) => fetchRegionResortsByStatus(
      pathInfo.uuid, ResortStatus.openResorts, page, REGION_RESORT_PAGE_SIZE,
    ),
    [pathInfo.uuid],
  );
  const fetchPreseasonPage = React.useCallback(
    (page: number) => fetchRegionResortsByStatus(
      pathInfo.uuid, ResortStatus.openingDates, page, REGION_RESORT_PAGE_SIZE,
    ),
    [pathInfo.uuid],
  );

  const open = useLoadMoreResorts({
    initial: resorts,
    fetchPage: fetchOpenPage,
    label: `region ${pathInfo.uuid} open-resorts`,
  });
  const preseason = useLoadMoreResorts({
    initial: openingDatesResorts,
    fetchPage: fetchPreseasonPage,
    label: `region ${pathInfo.uuid} open-resorts opening-dates`,
  });

  const openRows = open.rows;
  const preseasonRows = preseason.rows;

  // Only one of the two tables ever renders (the preseason list is
  // fetched only when nothing is open), so the union is exactly the set
  // of rows on screen. Judged on the server-rendered rows so a later page
  // cannot flip the decision mid-scroll.
  const showRegion = spansMultipleRegions([
    ...(resorts.data ?? []),
    ...(openingDatesResorts?.data ?? []),
  ]);

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={seoTitle}>{seoText}</SeoSection>

          {openRows.length > 0 && (
            <section>
              <Heading
                level={2}
                className="mb-3 font-heading text-lg text-secondary md:text-xl"
              >
                {t('resort.skiReport.open')}
              </Heading>
              <OpenResortsStatsTable
                rows={openRows}
                showRegion={showRegion}
                onSortIntent={open.loadAll}
                isLoading={open.isLoading}
              />
              <LoadMoreRows
                hasMore={open.hasMore}
                isLoading={open.isLoading}
                onLoadMore={open.loadMore}
              />
            </section>
          )}

          {openRows.length === 0 && preseasonRows.length > 0 && (
            // Off-season fallback: nothing is open yet, but the API
            // surfaced known opening dates for the upcoming season.
            <section>
              <Heading
                level={2}
                className="mb-3 font-heading text-lg text-secondary md:text-xl"
              >
                {t('region.openDates.projectedDate')}
              </Heading>
              <ProjectedOpeningTable
                rows={preseasonRows}
                linkTo={(r) => Routes.resortTripPlanningOverview(r)}
                dateLabel={t('region.openDates.projectedDate')}
                showRegion={showRegion}
                onSortIntent={preseason.loadAll}
                isLoading={preseason.isLoading}
              />
              <LoadMoreRows
                hasMore={preseason.hasMore}
                isLoading={preseason.isLoading}
                onLoadMore={preseason.loadMore}
              />
            </section>
          )}

          {openRows.length === 0 && preseasonRows.length === 0 && (
            <Text variant="default" className="text-muted-foreground">
              {t('region.openResorts.noResorts', { region: pathInfo.title })}
            </Text>
          )}

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={pathInfo.title}
            regionId={pathInfo.uuid}
          />

          <PlanYourTrip
            region={pathInfo}
            hasSkiRental={false}
            hasCarRental={false}
            hasFlights={false}
            isPartner={false}
          />
        </PageBody>
      </Main>
      <Side>
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd isRegion id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

// fetchProps moved to ./<filename>.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
